import { Avatar, Card, ErrorState, PageHeader, Tag, TextField } from '@sparkery/ui';
import { useProfile } from '../../lib/workspace.ts';
import './profile.css';

/**
 * Account details.
 *
 * The fields are disabled rather than absent, and the reason is a short label opposite
 * the card's title rather than a paragraph under the fields. A form that looks editable
 * and rejects every save is the worse failure; so is hiding the values, which are what
 * the viewer came to check.
 */
export function ProfileView() {
  const profile = useProfile();
  const data = profile.data;

  return (
    <>
      {/* Not "payout details": the card holds a name and an email, and a fixed-fee
          partner's fee is agreed outside the portal entirely. */}
      <PageHeader title="Profile" subtitle="Account details" />

      {profile.isError ? (
        <ErrorState
          title="Could not load your profile"
          {...(profile.error instanceof Error ? { detail: profile.error.message } : {})}
          onRetry={() => void profile.refetch()}
        />
      ) : null}

      <Card>
        <div className="profileIdentity">
          <Avatar initials={data?.initials ?? '··'} name={data?.name ?? ''} size="lg" />
          <div className="profileIdentityText">
            <h2 className="profileName">{data?.name ?? '—'}</h2>
            <p className="profileMeta">
              {data?.partnerName
                ? `${data.partnerName} · Partner ID ${data.partnerReference ?? '—'}`
                : 'Sparkery'}
            </p>
          </div>
          {data?.isCompanyAdmin ? <Tag tone="strong">Admin</Tag> : null}
          {data?.readOnlyReason ? (
            <span className="profileReadOnly">{data.readOnlyReason}</span>
          ) : null}
        </div>

        <div className="profileFields">
          <ReadOnlyField label="Name" value={data?.name ?? ''} />
          <ReadOnlyField label="Email" value={data?.email ?? ''} />
        </div>
      </Card>
    </>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  const id = `profile-${label.toLowerCase()}`;
  return <TextField id={id} label={label} value={value} disabled readOnly />;
}
